import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import type {
  FilterFieldKey,
  FilterOperator,
  FilterValue,
  TaskFilter,
} from '@/lib/types/filter'
import {
  FILTER_FIELD_DEFINITIONS,
  isDueDateOperator,
} from '@/lib/filters/fieldDefinitions'
import {
  addToNewGroup,
  newFilterId,
  normalizeGroups,
  replaceUnlockedFilters,
  upsertFilters,
  withIds,
} from '@/lib/filters/filterGroups'

/** Operators that take exactly one value (≥ High, before Fri). */
function isSingleValueOperator(operator: FilterOperator): boolean {
  return operator === 'at_least' || isDueDateOperator(operator)
}

/** Keep only the values that still make sense after an operator change. */
function valuesForOperator(
  filter: TaskFilter,
  operator: FilterOperator,
): Array<FilterValue> {
  // Due presets and due days are different kinds of value.
  if (
    filter.fieldKey === 'due' &&
    isDueDateOperator(filter.operator) !== isDueDateOperator(operator)
  ) {
    return []
  }
  if (operator === 'at_least' && filter.values.length > 1) {
    return [Math.min(...filter.values.map(Number))]
  }
  return filter.values
}

interface ProjectFilterStateValues {
  /** Every filter here has an `id` (see withIds). */
  filters: Record<string, Array<TaskFilter>>
  /**
   * "Combine with OR" was chosen and the new group has no filter yet
   * (design 9b). Not persisted: an empty group means nothing.
   */
  pendingGroup: Record<string, boolean>
}

/** Where a new filter goes: the plain list, an existing group, or a new one. */
export type FilterTargetGroup = number | 'new' | undefined

interface ProjectFilterState extends ProjectFilterStateValues {
  getFilters: (projectId: string) => Array<TaskFilter>
  loadSavedFilter: (projectId: string, filters: Array<TaskFilter>) => void
  loadSavedFilterMerged: (
    projectId: string,
    filters: Array<TaskFilter>,
    pinnedFields: Array<FilterFieldKey>,
  ) => void
  /** Add an empty chip; returns its id. */
  addFilter: (
    projectId: string,
    fieldKey: FilterFieldKey,
    options?: { group?: FilterTargetGroup; lockedFields?: Array<FilterFieldKey> },
  ) => string | null
  setOperator: (projectId: string, filterId: string, operator: FilterOperator) => void
  setValues: (projectId: string, filterId: string, values: Array<FilterValue>) => void
  toggleValue: (projectId: string, filterId: string, value: FilterValue) => void
  removeFilter: (projectId: string, filterId: string) => void
  /**
   * Put complete filters in place (quick filters, typed syntax): each
   * replaces the filter on the same property in the same group.
   */
  upsertFilters: (projectId: string, filters: Array<TaskFilter>) => void
  /** Replace every unlocked filter (typed input with OR groups). */
  replaceUnlockedFilters: (
    projectId: string,
    filters: Array<TaskFilter>,
    lockedFields: Array<FilterFieldKey>,
  ) => void
  setPendingGroup: (projectId: string, pending: boolean) => void
  clearFilters: (projectId: string) => void
}

const useProjectFilterStore = create<ProjectFilterState>()(
  devtools(
    persist(
      (set, get) => {
        const update = (
          projectId: string,
          fn: (current: Array<TaskFilter>) => Array<TaskFilter>,
        ) =>
          set((state) => ({
            filters: {
              ...state.filters,
              [projectId]: fn(state.filters[projectId] ?? []),
            },
          }))

        const updateOne = (
          projectId: string,
          filterId: string,
          fn: (f: TaskFilter) => TaskFilter,
        ) => update(projectId, (current) => current.map((f) => (f.id === filterId ? fn(f) : f)))

        return {
          filters: {},
          pendingGroup: {},

          getFilters: (projectId) => get().filters[projectId] ?? [],

          loadSavedFilter: (projectId, filters) => {
            update(projectId, () => normalizeGroups(withIds(filters)))
            get().setPendingGroup(projectId, false)
          },

          loadSavedFilterMerged: (projectId, filters, pinnedFields) => {
            update(projectId, (current) => {
              const pinned = current.filter(
                (f) => pinnedFields.includes(f.fieldKey) && f.group === undefined,
              )
              const savedNonPinned = filters.filter(
                (f) => !(pinnedFields.includes(f.fieldKey) && f.group === undefined),
              )
              return normalizeGroups(withIds([...pinned, ...savedNonPinned]))
            })
            get().setPendingGroup(projectId, false)
          },

          addFilter: (projectId, fieldKey, options) => {
            const fieldDef = FILTER_FIELD_DEFINITIONS[fieldKey]
            if (!fieldDef) return null
            const group = options?.group
            const filter: TaskFilter = {
              id: newFilterId(),
              fieldKey,
              operator: fieldDef.defaultOperator,
              values: [],
            }
            if (group === 'new') {
              update(projectId, (current) =>
                addToNewGroup(current, filter, options?.lockedFields),
              )
              get().setPendingGroup(projectId, false)
            } else {
              update(projectId, (current) => {
                // One chip per property per group.
                if (current.some((f) => f.fieldKey === fieldKey && f.group === group)) {
                  return current
                }
                return [...current, { ...filter, group }]
              })
            }
            return filter.id ?? null
          },

          setOperator: (projectId, filterId, operator) =>
            updateOne(projectId, filterId, (f) => ({
              ...f,
              operator,
              values: valuesForOperator(f, operator),
            })),

          setValues: (projectId, filterId, values) =>
            updateOne(projectId, filterId, (f) => ({ ...f, values })),

          toggleValue: (projectId, filterId, value) =>
            updateOne(projectId, filterId, (f) => {
              const has = f.values.includes(value)
              if (isSingleValueOperator(f.operator)) {
                return { ...f, values: has ? [] : [value] }
              }
              return {
                ...f,
                values: has ? f.values.filter((v) => v !== value) : [...f.values, value],
              }
            }),

          removeFilter: (projectId, filterId) =>
            update(projectId, (current) =>
              normalizeGroups(current.filter((f) => f.id !== filterId)),
            ),

          upsertFilters: (projectId, filters) =>
            update(projectId, (current) => upsertFilters(current, filters)),

          replaceUnlockedFilters: (projectId, filters, lockedFields) =>
            update(projectId, (current) =>
              replaceUnlockedFilters(current, filters, lockedFields),
            ),

          setPendingGroup: (projectId, pending) =>
            set((state) =>
              !!state.pendingGroup[projectId] === pending
                ? state
                : { pendingGroup: { ...state.pendingGroup, [projectId]: pending } },
            ),

          clearFilters: (projectId) => {
            update(projectId, () => [])
            get().setPendingGroup(projectId, false)
          },
        }
      },
      {
        name: 'moly-project-filters-v2',
        version: 1,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ filters: state.filters }),
        // v0 filters had no chip ids.
        migrate: (persisted) => {
          const old = (persisted ?? {}) as Partial<ProjectFilterStateValues>
          const filters: Record<string, Array<TaskFilter>> = {}
          for (const [key, list] of Object.entries(old.filters ?? {})) {
            filters[key] = withIds(list)
          }
          return { filters }
        },
      },
    ),
  ),
)

export default useProjectFilterStore
